import { addEventListener, getStackFrameList, getStackList, isErrorInstance } from '@fpkg/stats-utils';
import { BasePlugin, BaseStats, ErrorInfo, ErrorType, EventIds, EventLevel, mergePluginOption } from '@fpkg/stats-core';
import { checkErrorInfo } from '../utils';

export interface JsErrorOpt {
  // 与error的message匹配，忽略对应的JS错误。
  ignoreErrors?: (string | RegExp)[];
  // 当前发生的JS错误会和上一个错误比较，如果是同一个错误，是否不再上报。重复错误上报往往没有意义。
  // 为了避免循环上报，默认开启了去重。如果业务场景希望相同的错误能够重复上报，需要将此字段设置为false。
  dedupe?: boolean;
  event_level?: EventLevel; // 设置事件级别，error类型会上报顺丰云日志
}

/**
 * 捕获浏览器普通 JavaScript 错误
 */
export default class JsErrorPlugin implements BasePlugin {
  name = 'jsError';

  limit = 100;

  stats?: BaseStats;

  option: JsErrorOpt = {
    ignoreErrors: [],
    dedupe: true,
  };

  constructor(option: JsErrorOpt | null | boolean) {
    mergePluginOption(this.option, option);
  }

  setup(stats: BaseStats) {
    this.stats = stats;
    addEventListener('error', this.handlerJsErrorEvent.bind(this), true);
    addEventListener('unhandledrejection', this.handlerUnhandledrejectionEvent.bind(this), true);
  }

  /**
   * 捕获js error
   * @param jsErrorEvent
   */
  handlerJsErrorEvent(jsErrorEvent: Event, microApp?: string): ErrorInfo | null {
    // 微应用上报的event对象可能是iframe中的ErrorEvent，父应用不能用instanceof识别
    if (!(jsErrorEvent instanceof ErrorEvent) && !microApp) return null;

    // 同一个页面最多上报100次JS异常，防止循环错误不断的报错
    if (this.limit-- < 0) return null;

    const { message, filename, lineno, colno, error } = jsErrorEvent as ErrorEvent;

    const eventInfo: ErrorInfo = {
      event: EventIds.ErrorInfo,
      type: ErrorType.JsError,
      message,
      filename,
      lineno,
      colno,
      stack_list: getStackList(error),
      performance_time: Math.round(performance.now()),
      error_key: '',
      event_level: this.option.event_level || '',
    };

    if (microApp) {
      eventInfo.micro_app = microApp;
    }

    // 根据错误信息，错误文件，行列号，生成错误编码
    eventInfo.error_key = this.createErrorKey(eventInfo);

    if (checkErrorInfo(eventInfo, this.option.ignoreErrors, this.option.dedupe) && this.stats) {
      const stackFrames = getStackFrameList(error);

      this.stats.event(eventInfo);
      this.stats.$bus.$emit('ON_JS_ERROR', eventInfo, stackFrames);
      return eventInfo;
    }
    return null;
  }

  handleJsError(error: Error, microApp?: string, type: ErrorType = ErrorType.ManualHandleJsError): ErrorInfo | null {
    const stackFrames = getStackFrameList(error);
    const { colno: columnNumber, lineno: lineNumber, filename: fileName } = stackFrames[0] || {};

    const eventInfo: ErrorInfo = {
      event: EventIds.ErrorInfo,
      type,
      message: error.message,
      filename: fileName,
      lineno: lineNumber,
      colno: columnNumber,
      stack_list: getStackList(error),
      performance_time: Math.round(performance.now()),
      error_key: '',
      event_level: this.option.event_level || '',
    };

    if (microApp) {
      eventInfo.micro_app = microApp;
    }

    eventInfo.error_key = this.createErrorKey(eventInfo);

    if (checkErrorInfo(eventInfo, this.option.ignoreErrors, this.option.dedupe) && this.stats) {
      this.stats.event(eventInfo);
      this.stats.$bus.$emit('ON_JS_ERROR', eventInfo, stackFrames);
      return eventInfo;
    }
    return null;
  }

  /**
   * 捕获Promise reject unhandledrejection 异常
   */
  handlerUnhandledrejectionEvent(event: PromiseRejectionEvent, microApp?: string): ErrorInfo | null {
    const error = event ? event.reason : null;
    // 非Error类型的错误不上报
    if (!isErrorInstance(error)) return null;

    // 同一个页面最多上报100次JS异常，防止循环错误不断的报错
    if (this.limit-- < 0) return null;

    return this.handleJsError(error, microApp, ErrorType.UnhandledRejection);
  }

  createErrorKey(info: ErrorInfo): string {
    return info.type + info.message + info.filename + info.lineno + info.colno;
  }
}
